import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactSelect from 'components/base/ReactSelect';
import { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  ClearIndicatorProps,
  components,
  MultiValue,
  MultiValueProps,
  ValueContainerProps
} from 'react-select';
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

export interface BaseMultiDropDownItem {
  ID: string | number;
  Title: string;
  value: string;
  label: string;
}

interface BaseMultiDropdownProps {
  title: string;
  items: BaseMultiDropDownItem[];
  itemId: string | number;
  isFiltered: boolean;

  onItemChange: (
    itemId: string | number,
    selectedItems: BaseMultiDropDownItem[]
  ) => void;
}

const ClearIndicator = (props: ClearIndicatorProps) => (
  <components.ClearIndicator {...props} className="py-0 cursor-pointer">
    <FontAwesomeIcon icon={faXmark} className="text-900" />
  </components.ClearIndicator>
);

function BaseMultiDropdown({
  title,
  items,
  itemId,
  isFiltered,
  onItemChange
}: BaseMultiDropdownProps) {
  const [selected, setSelected] = useState<MultiValue<BaseMultiDropDownItem>>(
    []
  );
  const [dropdownKey, setDropdownKey] = useState(crypto.randomUUID());
  const tooltipId = useRef(`hidden-items-${crypto.randomUUID()}`);
  const [maxVisibleItems, setMaxVisibleItems] = useState(3);

  const [calculatedVisibleCount, setCalculatedVisibleCount] =
    useState(maxVisibleItems);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFiltered) {
      setSelected([]);
      setDropdownKey(crypto.randomUUID());
    }
  }, [isFiltered]);
  useEffect(() => {
    setSelected(items.filter(i => selected.some(c => c.ID === i.ID)));
  }, [items]);

  useEffect(() => {
    let visibleCount = maxVisibleItems;
    if (selected.length <= maxVisibleItems) {
      visibleCount = selected.length;
    }
    setCalculatedVisibleCount(visibleCount);
  }, [selected, maxVisibleItems]);

  const handleChange = (newValue: unknown) => {
    const typedValue = newValue as MultiValue<BaseMultiDropDownItem>;
    setSelected(typedValue);

    const selectedItems = typedValue.map(i => i as BaseMultiDropDownItem);
    onItemChange(itemId, selectedItems);
  };

  const options = items.map(i => ({
    ...i,
    value: i.ID,
    label: i.Title
  }));

  useEffect(() => {
    const updateItemsPerRow = () => {
      setMaxVisibleItems(1);
    };

    updateItemsPerRow();
    window.addEventListener('resize', updateItemsPerRow);

    return () => {
      window.removeEventListener('resize', updateItemsPerRow);
    };
  }, []);

  const CustomMultiValue = (props: MultiValueProps) => {
    const { index } = props;
    if (index >= calculatedVisibleCount) {
      return null;
    }
    return <components.MultiValue {...props} />;
  };

  const CustomValueContainer = (props: ValueContainerProps) => {
    const { children, getValue } = props;
    const values = getValue();
    const hiddenItemsCount = values.length - calculatedVisibleCount;
    return (
      <components.ValueContainer {...props}>
        {children}
        {hiddenItemsCount > 0 && (
          <OverlayTrigger
            overlay={
              <Tooltip id="overlay-trigger-example">
                <div style={{ padding: '5px' }}>
                  {selected.slice(calculatedVisibleCount).map((item, index) => (
                    <div key={item.ID || index} style={{ padding: '2px 0' }}>
                      {item.Title}
                    </div>
                  ))}
                </div>
              </Tooltip>
            }
          >
            <div
              data-tooltip-id={tooltipId.current}
              style={{
                backgroundColor: '#e6e6e6',
                borderRadius: '2px',
                padding: '0px',
                margin: '0px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                color: '#444'
              }}
            >
              +{hiddenItemsCount}
            </div>
          </OverlayTrigger>
        )}
      </components.ValueContainer>
    );
  };

  const customComponents = {
    ...animatedComponents,
    MultiValue: CustomMultiValue,
    ValueContainer: CustomValueContainer,
    ClearIndicator
  };

  return (
    <div ref={containerRef}>
      <ReactSelect
        key={dropdownKey}
        components={customComponents}
        closeMenuOnSelect={true}
        isMulti
        options={options}
        value={selected}
        placeholder={title}
        onChange={handleChange}
        styles={{
          multiValue: base => ({
            ...base,
            minWidth: '100px',
            maxWidth: '100px',
            flexShrink: 0
          }),
          valueContainer: base => ({
            ...base,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            padding: '2px 8px'
          }),
          multiValueLabel: base => ({
            ...base,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '400px'
          })
        }}
      />
    </div>
  );
}

export default BaseMultiDropdown;
